import _ from 'lodash';
import {
  registerLocale,
  setDefaultLocale
} from 'react-datepicker';
import { setDefaultOptions, isMatch, parse, format, startOfDay } from 'date-fns';
import enAU from 'date-fns/locale/en-AU';
import zhCN from 'date-fns/locale/zh-CN';
import { isAuthorized } from '../app/userImpersonate';

const localeMapping = {
  'en': enAU,
  'zh_CN': zhCN,
};

export const defaultDateFormat = 'dd/MM/yyyy';
export const fullDateFormat = 'yyyy-MM-dd HH:mm:ss';

let currentDateFormat = defaultDateFormat;

export const initAppLocale = (defaultLocale) => {
  _.values(localeMapping).map(locale => registerLocale(locale));
  setAppLocale(defaultLocale);
};

export const setAppLocale = (locale) => {
  const dateLocale = localeMapping[locale] ? localeMapping[locale] : localeMapping.en;
  setDefaultOptions({ locale: dateLocale });
  setDefaultLocale();
};

export const parseUserInfo = (userInfo) => {
  const parsedInfo = {
    ...userInfo,
    orgId: userInfo.usrOrgId,
    userId: userInfo.usrId,
    usrDateFormat: userInfo.usrDateFormat || defaultDateFormat,
    usrTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  currentDateFormat = parsedInfo.usrDateFormat;
  return parsedInfo;
};

export const smartSum = (numbers) => {
  const countDecimalPlaces = (num) => {
    const str = num.toString();
    const parts = str.split('.');
    return parts[1] ? parts[1].length : 0;
  };
  const maxDecimalPlaces = numbers.reduce((max, num) => {
    return Math.max(max, countDecimalPlaces(num));
  }, 0);
  const factor = Math.pow(10, maxDecimalPlaces);
  const sum = numbers.reduce((acc, num) => acc + num, 0);
  return Math.round(sum * factor) / factor;
};

export const convertToDate = (date) => _.isString(date) && isMatch(date, defaultDateFormat)
  ? parse(date, defaultDateFormat, new Date())
  : new Date(date);

export const formatDate = (date, defaultValue = '') => {
  if (!date) {
    return defaultValue;
  }
  return format(convertToDate(date), currentDateFormat);
};

export const toValidDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const modifyDate = (dateObject, newObject) => {
  dateObject.setTime(newObject.getTime());
  return dateObject;
};

export const downloadCsv = (csvContent, fileName) => {
  const csvBlob = new Blob([csvContent], { type: 'text/csv' });
  const blobUrl = URL.createObjectURL(csvBlob);
  const anchor = document.createElement('a');
  document.body.appendChild(anchor);
  anchor.href = blobUrl;
  anchor.setAttribute('download', fileName);
  anchor.click();
  anchor.parentNode.removeChild(anchor);
};

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const normalizeRecipientEmails = (emails = []) => {
  const uniqueEmails = new Map();

  emails.forEach((email) => {
    const trimmedEmail = String(email || '').trim();

    if (!trimmedEmail) {
      return;
    }

    const normalizedEmail = trimmedEmail.toLowerCase();

    if (!uniqueEmails.has(normalizedEmail)) {
      uniqueEmails.set(normalizedEmail, trimmedEmail);
    }
  });

  return Array.from(uniqueEmails.values());
};

export const displayTimeFromSeconds = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const displayNumber = (number) => `0${number}`.slice(-2);
  return `${displayNumber(minutes)}:${displayNumber(seconds - minutes * 60)}`;
};

export const exportCsv = (rows, fileName) => {
  const parseRow = (row) => row.map(text => {
    const val = _.trim(String(text), "\n")
      .replace(/"/g, '""')
      .replace(/[\n\r]/g, ' ');
    if (val.includes(',')) {
      return `"${val}"`;
    }
    return val;
  }).join(',');
  const csvContent = rows.map(parseRow).join('\n');
  downloadCsv(csvContent, fileName);
};

export const triggerFileDownload = (url, fileName) => {
  const anchor = document.createElement('a');
  document.body.appendChild(anchor);
  anchor.target = '_blank';
  anchor.href = url;
  anchor.setAttribute('download', fileName);
  anchor.click();
  anchor.parentNode.removeChild(anchor);
};

export const parseErrorMessage = (errorMsg) => {
  const regex = /<(\w+):([^>]+)>/g;
  const params = {};
  let match;
  let parsedMsg = errorMsg;
  while ((match = regex.exec(errorMsg)) !== null) {
    const [fullMatch, key, value] = match;
    params[key] = value;
    parsedMsg = parsedMsg.replace(fullMatch, `{{${key}}}`);
  }
  if (_.isEmpty(params)) {
    return false;
  }
  return {
    message: parsedMsg,
    params: params,
  };
};

export const transErrorMessage = (t, err) => {
  if (!err.message) {
    return t('Unkown error message');
  }
  if (err.status && err.status !== 400) {
    return err.message;
  }
  const parsed = parseErrorMessage(err.message);
  if (parsed) {
    return t(parsed.message, parsed.params);
  }
  return t(err.message);
};

export const formatApiErrorDates = (message) => {
  const parsed = parseErrorMessage(message);
  if (!parsed) return message;
  const dateKeys = ['fromDate', 'toDate'];
  return Object.entries(parsed.params).reduce((msg, [key, value]) => {
    const formatted = dateKeys.includes(key) ? (formatDate(value) || value) : value;
    return msg.replace(`{{${key}}}`, formatted);
  }, parsed.message);
};

export const datePickerClass = (isInvalid) => {
  if (isInvalid) {
    return {
      className: 'form-control is-invalid',
      wrapperClassName: 'is-invalid',
    };
  }
  return {
    className: 'form-control',
    wrapperClassName: 'is-valid',
  };
};

export const datePickerValue = (current, callback) => {
  const onChangeHandler = (dt) => {
    const val = dt && !isNaN(dt) ? format(startOfDay(dt), fullDateFormat) : null;
    callback(val);
  };
  return {
    selected: current ? new Date(current) : null,
    onChange: onChangeHandler
  };
};

export function formatArrayValueToString(t, listValue, maxDisplay = 2) {
  if (!listValue?.length) return "";

  if (listValue.length <= maxDisplay) {
    return listValue.join(", ");
  }

  const shown = listValue.slice(0, maxDisplay).join(", ");
  const remaining = listValue.length - maxDisplay;
  const valueType = remaining > 1 ? t('items') : t('item');
  return t('{{shown}} and {{remaining}} {{valueType}} left', {
    shown,
    remaining,
    valueType,
  });
}

export const numericKeydownHandler = (event) => {
  const allowedKeys = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
    'Tab',
  ];
  const isShortcut = event.ctrlKey || event.metaKey;
  if (allowedKeys.includes(event.key) || isShortcut || event.key.length !== 1) {
    return;
  }

  const { value, selectionStart, selectionEnd } = event.target;
  const start = selectionStart ?? value.length;
  const end = selectionEnd ?? value.length;
  const nextValue = `${value.slice(0, start)}${event.key}${value.slice(end)}`;
  if (!/^-?\d*\.?\d*$/.test(nextValue)) {
    event.preventDefault();
  }
};

export const getMonthDateFormat = (dateFormat) => {
  const matchRegx = dateFormat.match(/[-./]/);
  if (!matchRegx) {
    return dateFormat;
  }
  return dateFormat.split(matchRegx[0])
    .filter(path => path !== 'dd')
    .join(matchRegx[0]);
};

export const isAdminRole = (keycloakObject) => {
  const argCount = isAuthorized.length;
  const roles = ['cv_admin', 'cv_root'];
  // @todo remove if the story schedule faviorite report
  return argCount === 2 ? isAuthorized(keycloakObject, roles) : isAuthorized(roles);
}
